'use client';

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Stat {
  label: string;
  target: number;
  suffix: string;
  prefix?: string;
  icon: string;
  description: string;
}

const stats: Stat[] = [
  {
    label: 'Applications this week',
    target: 8340,
    suffix: '',
    icon: 'DocumentTextIcon',
    description: 'Senior professionals applied via remotejobehire',
  },
  {
    label: 'Companies hiring today',
    target: 143,
    suffix: '',
    icon: 'BuildingOfficeIcon',
    description: 'Distributed-first teams with open roles',
  },
  {
    label: 'Avg. days to first interview',
    target: 6,
    suffix: ' days',
    icon: 'CalendarDaysIcon',
    description: 'From application to hiring manager call',
  },
  {
    label: 'Median salary listed',
    target: 168,
    prefix: '$',
    suffix: 'k',
    icon: 'BanknotesIcon',
    description: 'Across all senior-level open positions',
  },
];

function useCountUp(target: number, duration: number, active: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!active) return;
    let start = 0;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(eased * target);
      setCount(current);
      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  }, [active, target, duration]);

  return count;
}

function StatCard({ stat, active }: { stat: Stat; active: boolean }) {
  const count = useCountUp(stat.target, 1800, active);

  return (
    <div className="counter-stat flex-1 px-8 py-10 text-center group">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-signal-blue/8 mb-4">
        <Icon name={stat.icon as any} size={20} className="text-signal-blue" />
      </div>
      <div className="text-3xl font-bold text-slate-dark tabular-nums mb-1">
        {stat.prefix || ''}{count.toLocaleString()}{stat.suffix}
      </div>
      <p className="text-sm font-semibold text-slate-pencil mb-1">{stat.label}</p>
      <p className="text-xs text-slate-light leading-relaxed">{stat.description}</p>
    </div>
  );
}

export default function CounterStrip() {
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white border-y border-fog-gray">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x divide-fog-gray">
          {stats.map((stat) => (
            <StatCard key={stat.label} stat={stat} active={active} />
          ))}
        </div>
      </div>
    </section>
  );
}